import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/product';
import categoryService from '../services/category';
import ServiceList from '../components/ServiceList';
import ProfessionalList from '../components/ProfessionalList';
import BookingsList from '../components/BookingsList';
import ClientsList from '../components/ClientsList';
import CalendarView from '../components/CalendarView';
import AdminCalendar from '../components/AdminCalendar';
// Force refresh
import SalesDashboard from '../components/SalesDashboard';
import ImageManager from '../components/ImageManager';
import MiniImageUploader from '../components/MiniImageUploader';
import ContentManager from '../components/ContentManager';
import { AdvancedColorPicker } from '../components/AdvancedColorPicker';
import api from '../services/api';
import { Plus, Edit2, Trash2, Package, Settings, LogOut, X, Check, AlertCircle, FileText } from 'lucide-react';
import { formatCurrency } from '../utils';
import type { LocationInventory } from '../types';

// Tipo local para imágenes del producto
interface ProductImageItem { id: number | string; image: string; is_main?: boolean }

// Helper functions para stock de variantes
const getVariantStockByLocation = (variant: any, location: 'SANTIAGO' | 'ANTOFAGASTA'): number => {
  if (!variant.location_inventory || !Array.isArray(variant.location_inventory)) {
    return 0;
  }
  const inv = variant.location_inventory.find((inv: LocationInventory) => inv.location === location);
  return inv?.quantity || 0;
};

// Helper para generar thumbnails de Supabase (optimización de carga)
const getSupabaseThumbnail = (url: string | null | undefined, width = 100): string => {
  if (!url) return '';
  // Si la URL es de Supabase, agregar transformación de imagen
  if (url.includes('supabase.co/storage')) {
    // Formato: /render/image/public/{path}?width=100&quality=75
    const urlParts = url.split('/storage/v1/object/public/');
    if (urlParts.length === 2) {
      return `${urlParts[0]}/storage/v1/render/image/public/${urlParts[1]}?width=${width}&quality=75`;
    }
  }
  return url;
};

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState('products');
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [newVariantColor, setNewVariantColor] = useState('#000000');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    discount_percentage: '',
    stock: '',
    stock_santiago: '',
    stock_antofagasta: '',
    sku: '',
    low_stock_threshold: '5',
    ingredients: '',
    usage_instructions: '',
    benefits: '',
    size: '',
    hair_type: '',
    category_ids: [] as number[],
    image: '',
    uploaded_images: [] as string[],
    is_featured: false,
    is_best_selling: false,
    is_active: true,
    use_variants: false,
    variants: [] as any[]
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [showForm, setShowForm] = useState(false);
  // Estado y errores para crear categorías (Django)
  const [categoryForm, setCategoryForm] = useState<{ name: string; slug: string; description: string; is_active: boolean; parent_id?: number }>({
    name: '',
    slug: '',
    description: '',
    is_active: true,
    parent_id: undefined
  });
  const [categoryErrors, setCategoryErrors] = useState<string[]>([]);
  const [creatingCategory, setCreatingCategory] = useState<boolean>(false);
  const [showCategoryModal, setShowCategoryModal] = useState<boolean>(false);
  const [editingCategory, setEditingCategory] = useState<any | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<'antofagasta' | 'santiago'>('antofagasta');

  // Estado de imágenes existentes del producto en edición
  const [existingImages, setExistingImages] = useState<ProductImageItem[]>([]);

  const [editingVariantIndex, setEditingVariantIndex] = useState<number | null>(null);

  // (Servicios ahora manejados por componentes separados)

  useEffect(() => {
    if (!showForm) {
      console.trace(">>> CERRANDO MODAL - useEffect (showForm=false) - Cierre no detectado <<<");
    }
  }, [showForm]);

  // Cargar imágenes del producto cuando se abre el formulario de edición
  useEffect(() => {
    const fetchImages = async () => {
      if (showForm && editingProduct?.id) {
        try {
          const res = await api.get('products/images/', { params: { product: editingProduct.id } });
          const rawList = Array.isArray(res.data) ? res.data : res.data?.results;
          const imgs: ProductImageItem[] = (rawList || []).map((it: any) => ({ id: it.id, image: it.image, is_main: it.is_main }));
          setExistingImages(imgs);
        } catch (err) {
          console.warn('[AdminPanel] No se pudieron cargar imágenes existentes del producto:', err);
          setExistingImages([]);
        }
      } else {
        setExistingImages([]);
      }
    };
    fetchImages();
  }, [showForm, editingProduct?.id]);

  // Supabase auth check eliminado; se usa token local en otro efecto
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    // Inicia la carga en segundo plano y libera el spinner rápido
    loadData().catch((error) => {
      console.error('Error al cargar datos:', error);
    });
    setLoading(false);
  }, [navigate]);

  const loadData = async () => {
    console.time('⏱️ Carga total de datos'); // DEBUG: Medir tiempo
    try {
      setLoadingProducts(true);
      setLoadingCategories(true);
      
      console.time('⏱️ Request productos'); // DEBUG: Medir request
      const [productsRes, categoriesRes] = await Promise.allSettled([
        productService.getAll({ page_size: 30, ordering: '-created_at' }), // Ordenar por más recientes primero
        // Cargar todas las categorías (raíces + subcategorías)
        categoryService.getAll({ parent: '' }),
      ]);
      console.timeEnd('⏱️ Request productos'); // DEBUG: Fin medición

      if (productsRes.status === 'fulfilled') {
        const productsData = productsRes.value;
        console.log('✅ Productos recibidos:', productsData.results?.length); // DEBUG
        setProducts(productsData.results || []);
      } else {
        console.error('Error al cargar productos:', productsRes.reason);
        setProducts([]);
        // Verificar si es error de autenticación
        const error = productsRes.reason as any;
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          console.warn('Sesión expirada detectada en productos');
          sessionStorage.setItem('session_expired_message', 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
          navigate('/admin/login');
          return;
        }
      }

      if (categoriesRes.status === 'fulfilled') {
        const categoriesData = categoriesRes.value;
        setCategories(categoriesData || []);
      } else {
        console.error('Error al cargar categorías de productos:', categoriesRes.reason);
        setCategories([]);
        // Verificar si es error de autenticación
        const error = categoriesRes.reason as any;
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          console.warn('Sesión expirada detectada en categorías');
          sessionStorage.setItem('session_expired_message', 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
          navigate('/admin/login');
          return;
        }
      }
    } catch (error: any) {
      console.error('Error al cargar datos:', error);
      // Si hay error de autenticación, redirigir
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.warn('Sesión expirada detectada en catch general');
        sessionStorage.setItem('session_expired_message', 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        navigate('/admin/login');
      }
    } finally {
      setLoadingProducts(false);
      setLoadingCategories(false);
      console.timeEnd('⏱️ Carga total de datos'); // DEBUG: Fin medición total
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    navigate('/admin/login');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;
  
    // Limpiar error específico de este campo
    if (formErrors[name]) {
      setFormErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  
    if (name === 'price' || name === 'stock' || name === 'discount_percentage') {
      const regex = (name === 'price' || name === 'discount_percentage') ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]*$/;
      if (value === '' || regex.test(value)) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
        }));
      }
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      price: '',
      discount_percentage: '',
      stock: '',
      stock_santiago: '',
      stock_antofagasta: '',
      sku: '',
      low_stock_threshold: '5',
      ingredients: '',
      usage_instructions: '',
      benefits: '',
      size: '',
      hair_type: '',
      category_ids: [],
      image: '',
      uploaded_images: [],
      is_featured: false,
      is_best_selling: false,
      is_active: true,
      use_variants: false,
      variants: []
    });
    setEditingProduct(null);
    setFormErrors({});
    console.trace(">>> CERRANDO MODAL - resetForm <<<");
    setShowForm(false);
  };

  const handleEditProduct = (product: any) => {
    setEditingProduct(product);
    
    // Obtener stock por ubicación
    const santiago = product?.stock_locations?.find((loc: any) => loc.location === 'SANTIAGO');
    const antofagasta = product?.stock_locations?.find((loc: any) => loc.location === 'ANTOFAGASTA');
    
    // Mapear variantes con stock por ubicación desde location_inventory
    const mappedVariants = (product?.variants || []).map((variant: any) => {
      const stgoInv = variant.location_inventory?.find((inv: any) => inv.location === 'SANTIAGO');
      const antofInv = variant.location_inventory?.find((inv: any) => inv.location === 'ANTOFAGASTA');
      
      return {
        ...variant,
        stock_santiago: stgoInv?.quantity || 0,
        stock_antofagasta: antofInv?.quantity || 0
      };
    });
    
    setFormData({
      name: product?.name ?? '',
      slug: product?.slug ?? '',
      description: product?.description ?? '',
      price: product?.price != null ? product.price.toString() : '',
      discount_percentage: product?.discount_percentage != null ? product.discount_percentage.toString() : '',
      stock: product?.stock != null ? product.stock.toString() : '',
      stock_santiago: santiago?.quantity != null ? santiago.quantity.toString() : '0',
      stock_antofagasta: antofagasta?.quantity != null ? antofagasta.quantity.toString() : '0',
      sku: product?.sku ?? '',
      low_stock_threshold: product?.low_stock_threshold != null ? product.low_stock_threshold.toString() : '5',
      ingredients: product?.ingredients ?? '',
      usage_instructions: product?.usage_instructions ?? '',
      benefits: product?.benefits ?? '',
      size: product?.size ?? '',
      hair_type: product?.hair_type ?? '',
      category_ids: product?.categories?.map((c: any) => c.id) || [],
      image: product?.image ?? '',
      uploaded_images: [],
      is_featured: !!product?.is_featured,
      is_best_selling: !!product?.is_best_selling,
      is_active: product?.is_active ?? true,
      use_variants: !!product?.use_variants,
      variants: mappedVariants
    });
    setShowForm(true);
  };

  const handleDeleteProduct = async (slug: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
    
    try {
      await productService.delete(slug);
      await loadData();
    } catch (error) {
      console.error('Error al eliminar producto:', error);
    }
  };

  // Eliminar categoría y refrescar lista
  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    try {
      await categoryService.delete(id);
      await loadData();
    } catch (error) {
      console.error('Error al eliminar categoría:', error);
    }
  };

  // Editar categoría
  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    const parentId = category.parent && typeof category.parent === 'object' ? category.parent.id : category.parent;
    setCategoryForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      is_active: category.is_active,
      parent_id: parentId || undefined
    });
    setShowCategoryModal(true);
  };

  // Crear o Editar categoría (modal)
  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreatingCategory(true);
      setCategoryErrors([]);
      
      const payload = {
        name: categoryForm.name.trim(),
        slug: categoryForm.slug.trim(),
        description: categoryForm.description?.trim() || undefined,
        is_active: categoryForm.is_active,
        parent: categoryForm.parent_id || null
      };

      if (editingCategory) {
        await categoryService.update(editingCategory.id, payload);
      } else {
        await categoryService.create(payload);
      }

      setCategoryForm({ name: '', slug: '', description: '', is_active: true, parent_id: undefined });
      setEditingCategory(null);
      setShowCategoryModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Error guardando categoría:', err);
      const msg = err?.response?.data;
      const errs: string[] = [];
      if (msg && typeof msg === 'object') {
        Object.keys(msg).forEach((k) => {
          const val = (msg as any)[k];
          if (Array.isArray(val) && val.length) errs.push(String(val[0]));
          else if (typeof val === 'string') errs.push(val);
        });
      }
      setCategoryErrors(errs.length ? errs : ['Error al guardar categoría']);
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleAddVariant = (variant: any) => {
    if (editingVariantIndex !== null) {
      // Actualizar variante existente - MANTENER ID ORIGINAL
      setFormData(prev => {
        const updatedVariants = [...prev.variants];
        updatedVariants[editingVariantIndex] = { 
          ...updatedVariants[editingVariantIndex], // Mantiene el ID original
          ...variant 
        };
        return { ...prev, variants: updatedVariants };
      });
      setEditingVariantIndex(null);
    } else {
      // Agregar nueva variante - SIN ID (el backend lo asignará)
      setFormData(prev => ({
        ...prev,
        variants: [...prev.variants, variant] // No agregar ID temporal
      }));
    }
  };

  const handleEditVariant = (index: number) => {
    const variant = formData.variants[index];
    setEditingVariantIndex(index);
    
    // Rellenar el formulario de variantes
    const nameInput = document.getElementById('new_variant_name') as HTMLInputElement;
    // value field removed
    const colorInput = document.getElementById('new_variant_color') as HTMLInputElement;
    const imageInput = document.getElementById('new_variant_image') as HTMLInputElement;
    const stockStgoInput = document.getElementById('new_variant_stock_santiago') as HTMLInputElement;
    const stockAntofInput = document.getElementById('new_variant_stock_antofagasta') as HTMLInputElement;

    if (nameInput) nameInput.value = variant.name || 'Color';
    // removed: value input prefill
    if (colorInput) {
      colorInput.value = variant.color_hex || '#000000';
      setNewVariantColor(variant.color_hex || '#000000');
    }
    if (imageInput) imageInput.value = variant.image || '';
    if (stockStgoInput) stockStgoInput.value = (variant.stock_santiago || 0).toString();
    if (stockAntofInput) stockAntofInput.value = (variant.stock_antofagasta || 0).toString();
  };

  const handleCancelEditVariant = () => {
    setEditingVariantIndex(null);
    
    // Limpiar formulario
    const nameInput = document.getElementById('new_variant_name') as HTMLInputElement;
    // value field removed
    const colorInput = document.getElementById('new_variant_color') as HTMLInputElement;
    const imageInput = document.getElementById('new_variant_image') as HTMLInputElement;
    const stockStgoInput = document.getElementById('new_variant_stock_santiago') as HTMLInputElement;
    const stockAntofInput = document.getElementById('new_variant_stock_antofagasta') as HTMLInputElement;

    if (nameInput) nameInput.value = 'Color';
    // removed: value input cleanup
    if (colorInput) {
      colorInput.value = '#000000';
      setNewVariantColor('#000000');
    }
    if (imageInput) imageInput.value = '';
    if (stockStgoInput) stockStgoInput.value = '0';
    if (stockAntofInput) stockAntofInput.value = '0';
  };

  const handleRemoveVariant = (index: number) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleDeleteGalleryImage = async (imageId: string | number) => {
    if (!window.confirm('¿Quitar esta imagen de la galería? (La imagen permanecerá en la biblioteca)')) return;
    try {
      // keep_file=true asegura que solo se borre el registro de la BD, no el archivo en Supabase
      await api.delete(`products/images/${imageId}/?keep_file=true`);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      alert('Error al eliminar la imagen');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setFormErrors({});
    
    console.log('📝 Formulario enviado con datos:', {
      name: formData.name,
      use_variants: formData.use_variants,
      variants: formData.variants,
      stock_santiago: formData.stock_santiago,
      stock_antofagasta: formData.stock_antofagasta
    });

    // Validación por campo
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      console.error('❌ Errores de validación:', errors);
      setFormErrors(errors);
      return;
    }

    const name = formData.name.trim();
    const description = (formData.description || '').trim();
    const image = (formData.image || '').trim();

    const payload: any = {
      name,
      description: description || null,
      price: parseFloat(String(formData.price)),
      discount_percentage: formData.discount_percentage ? parseInt(String(formData.discount_percentage), 10) : 0,
      sku: formData.sku || null,
      low_stock_threshold: parseInt(String(formData.low_stock_threshold), 10),
      ingredients: formData.ingredients || null,
      usage_instructions: formData.usage_instructions || null,
      benefits: formData.benefits || null,
      size: formData.size || null,
      hair_type: formData.hair_type || null,
      category_ids: formData.category_ids,
      image: image || null, // La imagen ahora es opcional
      uploaded_images: formData.uploaded_images,
      is_featured: !!formData.is_featured,
      is_best_selling: !!formData.is_best_selling,
      is_active: !!formData.is_active,
      use_variants: !!formData.use_variants,
      variants: formData.variants
    };
    
    // Solo enviar stock base si NO usa variantes
    if (!formData.use_variants) {
      payload.stock = parseInt(String(formData.stock_santiago || '0'), 10) + parseInt(String(formData.stock_antofagasta || '0'), 10);
      payload.stock_santiago = parseInt(String(formData.stock_santiago || '0'), 10);
      payload.stock_antofagasta = parseInt(String(formData.stock_antofagasta || '0'), 10);
    }
    
    // Debug: verificar lo que se está enviando
    console.log('🚀 PAYLOAD que se enviará al backend:', payload);

    try {
      if (editingProduct) {
        await productService.update(editingProduct.slug, payload);
        console.log('Producto actualizado exitosamente');
      } else {
        await productService.create(payload);
      }

      resetForm();
      await loadData();
    } catch (error: any) {
      console.error('❌ Error al guardar producto:', error);
      console.error('📋 Detalles del error:', {
        message: error?.message,
        response: error?.response,
        data: error?.response?.data,
        status: error?.response?.status
      });
      
      const data = error?.response?.data;
      const status = error?.response?.status;

      // Mapear errores del backend si son por campo (DRF suele devolver dict)
      if (data && typeof data === 'object') {
        const nextErrors: { [key: string]: string } = {};
        Object.keys(data).forEach((key) => {
          const val = (data as any)[key];
          if (Array.isArray(val) && val.length) nextErrors[key] = String(val[0]);
          else if (typeof val === 'string') nextErrors[key] = val;
        });
        console.error('🔴 Errores de validación:', nextErrors);
        setFormErrors(nextErrors);
      } else if (status === 400) {
        setFormErrors({ general: 'Solicitud inválida' });
      } else {
        setFormErrors({ general: 'Error desconocido al guardar' });
      }
    }
  };

  function validateForm(): { [key: string]: string } {
    const errors: { [key: string]: string } = {};
    if (!formData.name.trim()) errors.name = 'El nombre es obligatorio';
    if (!formData.price) errors.price = 'El precio es obligatorio';
    
    // Solo validar stock base si NO usa variantes
    if (!formData.use_variants) {
      if (!formData.stock_santiago && !formData.stock_antofagasta) {
        errors.stock_santiago = 'Debe especificar stock en al menos una ubicación';
      }
    }
    
    if (formData.category_ids.length === 0) errors.category_ids = 'Debes seleccionar al menos una categoría';
    return errors;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header minimalista */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
                <Settings className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Panel de Administración</h1>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Branch Selector */}
        {(activeTab === 'services' || activeTab === 'professionals' || activeTab === 'bookings' || activeTab === 'calendar' || activeTab === 'sales') && (
          <div className="mb-6 flex justify-center">
            <div className="bg-white p-1 rounded-lg border border-gray-200 inline-flex">
              <button
                onClick={() => setSelectedBranch('antofagasta')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedBranch === 'antofagasta'
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Sede Antofagasta
              </button>
              <button
                onClick={() => setSelectedBranch('santiago')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedBranch === 'santiago'
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Sede Santiago
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === 'products' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
          >
            Productos
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === 'categories' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
          >
            Categorías
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === 'services' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
          >
            Servicios
          </button>
          <button
            onClick={() => setActiveTab('professionals')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === 'professionals' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
          >
            Profesionales
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === 'bookings' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
          >
            Reservas
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === 'clients' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
          >
            Clientes
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === 'calendar' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
          >
            Calendario
          </button>
          <button
            onClick={() => setActiveTab('sales')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === 'sales' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
          >
            Ventas
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap flex items-center gap-2 ${activeTab === 'content' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
          >
            <FileText className="w-4 h-4" />
            Contenido
          </button>
        </div>

        {/* Contenido */}
        {activeTab === 'products' ? (
          <div>
            {/* Barra de acciones */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
              <div className="flex gap-4 w-full md:w-auto">
                <input
                  type="text"
                  placeholder="Buscar productos..."
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  onChange={(e) => {
                    const term = e.target.value.toLowerCase();
                    if (!term) {
                      loadData(); // Recargar si está vacío
                      return;
                    }
                    const filtered = products.filter(p => 
                      p.name.toLowerCase().includes(term) || 
                      p.description?.toLowerCase().includes(term) ||
                      p.sku?.toLowerCase().includes(term)
                    );
                    setProducts(filtered);
                  }}
                />
                <select
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  onChange={(e) => {
                    const catId = e.target.value;
                    if (!catId) {
                      loadData();
                      return;
                    }
                    // Filtrar localmente por ahora (idealmente backend)
                    // Asumiendo que products tiene categories populated
                    // Si no, habría que recargar del backend con filtro
                    // Pero loadData carga todos, así que filtramos localmente
                    // Para filtrar correctamente, necesitamos recargar todos primero y luego filtrar
                    // O mejor, llamar al backend con filtro
                    productService.getAll({ categories: catId }).then(res => setProducts(res.results || []));
                  }}
                >
                  <option value="">Todas las categorías</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => { setShowForm(true); setEditingProduct(null); setFormData(prev => ({ ...prev, image: '' })); }}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 shadow-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Nuevo Producto</span>
              </button>
            </div>

            {/* Formulario de producto */}
            {showForm && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">{editingProduct ? 'Editar Producto' : 'Crear Producto'}</h2>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Tabs de navegación */}
                <div className="border-b border-gray-200">
                  <nav className="flex space-x-8 px-6" aria-label="Tabs">
                    {['Información Básica', 'Inventario', 'Multimedia', 'Detalles del Producto'].map((tab, idx) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => {/* TODO: Implementar tabs */}}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                          idx === 0
                            ? 'border-gray-900 text-gray-900'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </nav>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                  {/* SECCIÓN: Información Básica */}
                  <div className="space-y-6 mb-8">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold">1</div>
                      <h3 className="text-lg font-semibold text-gray-900">Información Básica</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                          Nombre del Producto <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all text-base`}
                          placeholder="Ej: Ultimate Repair Shampoo"
                          required
                        />
                        {formErrors.name && (
                          <p className="mt-2 text-sm text-red-600 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {formErrors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-900 mb-2">
                          Descripción
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          rows={4}
                          value={formData.description}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all text-base"
                          placeholder="Describe las características principales del producto..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* SECCIÓN: Precio e Inventario */}
                  <div className="space-y-6 mb-8 pt-8 border-t border-gray-200">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold">2</div>
                      <h3 className="text-lg font-semibold text-gray-900">Precio e Inventario</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="md:col-span-1">
                        <label htmlFor="price" className="block text-sm font-medium text-gray-900 mb-2">
                          Precio <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            id="price"
                            name="price"
                            type="text"
                            value={formData.price}
                            onChange={handleInputChange}
                            className={`w-full pl-8 pr-4 py-3 border ${formErrors.price ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all text-base`}
                            placeholder="0"
                            required
                          />
                        </div>
                        {formErrors.price && (
                          <p className="mt-2 text-sm text-red-600">{formErrors.price}</p>
                        )}
                      </div>

                      <div className="md:col-span-1">
                        <label htmlFor="discount_percentage" className="block text-sm font-medium text-gray-900 mb-2">
                          Descuento
                        </label>
                        <div className="relative">
                          <input
                            id="discount_percentage"
                            name="discount_percentage"
                            type="number"
                            min="0"
                            max="100"
                            value={formData.discount_percentage}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all text-base"
                            placeholder="0"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>

                      <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Precio Final
                        </label>
                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-base font-semibold text-gray-900">
                          ${formData.price && formData.discount_percentage ? 
                            (parseFloat(formData.price) * (1 - parseFloat(formData.discount_percentage || '0') / 100)).toFixed(0) : 
                            formData.price || '0'}
                        </div>
                      </div>
                    </div>

                    {/* Stock por ubicación con diseño de tarjetas */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-semibold text-gray-900">Stock por Ubicación</h4>
                        {formData.use_variants && formData.variants.length > 0 && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                            Stock gestionado por variante
                          </span>
                        )}
                      </div>
                      
                      {/* Mensaje informativo cuando hay variantes */}
                      {formData.use_variants && formData.variants.length > 0 && (
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-900 mb-1">
                                📦 Este producto usa variantes
                              </p>
                              <p className="text-xs text-blue-700">
                                El stock se gestiona individualmente para cada variante (color, talla, etc.). 
                                Para modificar el stock, edita cada variante haciendo clic en el ícono de lápiz ✏️.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Mensaje cuando se activan variantes por primera vez */}
                      {formData.use_variants && formData.variants.length === 0 && (parseInt(formData.stock_santiago || '0') > 0 || parseInt(formData.stock_antofagasta || '0') > 0) && (
                        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-amber-900 mb-1">
                                ⚡ Migración automática de stock
                              </p>
                              <p className="text-xs text-amber-700">
                                Al crear la primera variante, el stock actual se transferirá automáticamente a esa variante. 
                                No perderás ningún producto en el proceso.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className={`bg-white border-2 rounded-lg p-4 transition-all ${
                          formData.use_variants && formData.variants.length > 0 
                            ? 'border-gray-200 opacity-60 cursor-not-allowed' 
                            : 'border-gray-300 hover:border-gray-900'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-900">📍 Santiago</span>
                            <Package className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            id="stock_santiago"
                            name="stock_santiago"
                            type="number"
                            min="0"
                            value={formData.stock_santiago}
                            onChange={handleInputChange}
                            disabled={formData.use_variants && formData.variants.length > 0}
                            className={`w-full px-4 py-2 border border-gray-300 rounded-lg text-2xl font-bold text-center transition-all ${
                              formData.use_variants && formData.variants.length > 0
                                ? 'bg-gray-100 cursor-not-allowed'
                                : 'focus:ring-2 focus:ring-gray-900 focus:border-transparent'
                            }`}
                            placeholder="0"
                            required={!formData.use_variants}
                          />
                          <p className="text-xs text-gray-500 text-center mt-2">unidades disponibles</p>
                        </div>

                        <div className={`bg-white border-2 rounded-lg p-4 transition-all ${
                          formData.use_variants && formData.variants.length > 0 
                            ? 'border-gray-200 opacity-60 cursor-not-allowed' 
                            : 'border-gray-300 hover:border-gray-900'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-900">📍 Antofagasta</span>
                            <Package className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            id="stock_antofagasta"
                            name="stock_antofagasta"
                            type="number"
                            min="0"
                            value={formData.stock_antofagasta}
                            onChange={handleInputChange}
                            disabled={formData.use_variants && formData.variants.length > 0}
                            className={`w-full px-4 py-2 border border-gray-300 rounded-lg text-2xl font-bold text-center transition-all ${
                              formData.use_variants && formData.variants.length > 0
                                ? 'bg-gray-100 cursor-not-allowed'
                                : 'focus:ring-2 focus:ring-gray-900 focus:border-transparent'
                            }`}
                            placeholder="0"
                            required={!formData.use_variants}
                          />
                          <p className="text-xs text-gray-500 text-center mt-2">unidades disponibles</p>
                        </div>
                      </div>
                      
                      {/* Stock total visual */}
                      {!formData.use_variants && (
                        <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Stock Total</span>
                            <span className="text-2xl font-bold text-gray-900">
                              {(parseInt(formData.stock_santiago || '0') + parseInt(formData.stock_antofagasta || '0'))}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Categorías con diseño de badges */}
                  <div className="space-y-6 mb-8">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-3">
                        Categorías <span className="text-red-500">*</span>
                      </label>
                      {loadingCategories ? (
                        <p className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">Cargando categorías...</p>
                      ) : (
                        <div className="flex flex-wrap gap-3">
                          {categories.map(category => {
                            const isSelected = formData.category_ids.includes(category.id);
                            return (
                              <button
                                key={category.id}
                                type="button"
                                onClick={() => {
                                  setFormData(prev => {
                                    const current = prev.category_ids;
                                    if (isSelected) {
                                      return { ...prev, category_ids: current.filter(id => id !== category.id) };
                                    } else {
                                      return { ...prev, category_ids: [...current, category.id] };
                                    }
                                  });
                                }}
                                className={`px-4 py-2 rounded-lg border-2 font-medium transition-all ${
                                  isSelected
                                    ? 'bg-gray-900 text-white border-gray-900'
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                                }`}
                              >
                                {isSelected && <Check className="w-4 h-4 inline mr-2" />}
                                {category.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {formErrors.category_ids && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {formErrors.category_ids}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* SECCIÓN: Detalles del Producto */}
                  <div className="space-y-6 mb-8 pt-8 border-t border-gray-200">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold">4</div>
                      <h3 className="text-lg font-semibold text-gray-900">Detalles del Producto</h3>
                    </div>

                    {/* Información adicional en grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="size" className="block text-sm font-medium text-gray-900 mb-2">Tamaño/Presentación</label>
                        <input
                          id="size"
                          name="size"
                          type="text"
                          value={formData.size}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          placeholder="Ej: 50 ml, 250 ml, 1L"
                        />
                      </div>
                      <div>
                        <label htmlFor="hair_type" className="block text-sm font-medium text-gray-900 mb-2">Tipo de Cabello</label>
                        <input
                          id="hair_type"
                          name="hair_type"
                          type="text"
                          value={formData.hair_type}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          placeholder="Ej: Todo tipo, Seco, Graso"
                        />
                      </div>
                      <div>
                        <label htmlFor="sku" className="block text-sm font-medium text-gray-900 mb-2">SKU (Código)</label>
                        <input
                          id="sku"
                          name="sku"
                          type="text"
                          value={formData.sku}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          placeholder="Código único del producto"
                        />
                      </div>
                      <div>
                        <label htmlFor="low_stock_threshold" className="block text-sm font-medium text-gray-900 mb-2">Alerta Stock Bajo</label>
                        <input
                          id="low_stock_threshold"
                          name="low_stock_threshold"
                          type="number"
                          value={formData.low_stock_threshold}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          placeholder="5"
                        />
                      </div>
                    </div>

                    {/* Campos de texto largo en acordeón */}
                    <div className="space-y-4">
                      <details className="group bg-gray-50 border border-gray-200 rounded-lg">
                        <summary className="px-4 py-3 cursor-pointer font-medium text-gray-900 flex items-center justify-between hover:bg-gray-100 transition-colors">
                          Ingredientes
                          <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <div className="px-4 pb-4 pt-2">
                          <textarea
                            id="ingredients"
                            name="ingredients"
                            rows={4}
                            value={formData.ingredients}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                            placeholder="Lista completa de ingredientes del producto..."
                          />
                        </div>
                      </details>

                      <details className="group bg-gray-50 border border-gray-200 rounded-lg">
                        <summary className="px-4 py-3 cursor-pointer font-medium text-gray-900 flex items-center justify-between hover:bg-gray-100 transition-colors">
                          Modo de Uso
                          <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <div className="px-4 pb-4 pt-2">
                          <textarea
                            id="usage_instructions"
                            name="usage_instructions"
                            rows={4}
                            value={formData.usage_instructions}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                            placeholder="1. Aplicar sobre cabello húmedo...&#10;2. Masajear suavemente...&#10;3. Enjuagar..."
                          />
                        </div>
                      </details>

                      <details className="group bg-gray-50 border border-gray-200 rounded-lg">
                        <summary className="px-4 py-3 cursor-pointer font-medium text-gray-900 flex items-center justify-between hover:bg-gray-100 transition-colors">
                          Beneficios
                          <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <div className="px-4 pb-4 pt-2">
                          <textarea
                            id="benefits"
                            name="benefits"
                            rows={4}
                            value={formData.benefits}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                            placeholder="• Repara el cabello dañado&#10;• Hidratación profunda&#10;• Brillo natural..."
                          />
                        </div>
                      </details>
                    </div>
                  </div>

                  {/* SECCIÓN: Multimedia */}
                  <div className="space-y-6 mb-8 pt-8 border-t border-gray-200">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold">3</div>
                      <h3 className="text-lg font-semibold text-gray-900">Multimedia</h3>
                    </div>

                    {/* Vista previa de imagen principal */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Imagen principal grande */}
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-900 mb-3">
                          Imagen Principal <span className="text-red-500">*</span>
                        </label>
                        <div className="relative aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden group hover:border-gray-400 transition-all">
                          {formData.image ? (
                            <>
                              <img 
                                src={formData.image} 
                                alt="Principal" 
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                                  className="opacity-0 group-hover:opacity-100 bg-red-600 text-white px-4 py-2 rounded-lg transition-opacity"
                                >
                                  Eliminar
                                </button>
                              </div>
                            </>
                          ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                              <Package className="w-16 h-16 mb-2" />
                              <p className="text-sm">Haz clic para seleccionar imagen</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Selector de imagen principal */}
                        <div className="mt-3">
                          <ImageManager
                            product={editingProduct ?? undefined}
                            existingImages={existingImages}
                            setExistingImages={setExistingImages}
                            currentImage={formData.image}
                            onImageSelect={(imageUrl) => setFormData(prev => ({ ...prev, image: imageUrl }))}
                          />
                        </div>
                      </div>

                      {/* Galería de miniaturas */}
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-900 mb-3">
                          Galería
                        </label>
                        <div className="space-y-3">
                          {existingImages.slice(0, 4).map((img, idx) => (
                            <div key={idx} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200 group hover:border-gray-400 transition-all">
                              <img src={img.image} alt={`Galería ${idx + 1}`} className="w-full h-full object-cover" />
                              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteGalleryImage(img.id)}
                                  className="bg-white rounded-full p-1 shadow-lg hover:bg-red-50 transition-colors"
                                >
                                  <X className="w-4 h-4 text-red-600" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Botón para agregar más */}
                        <div className="mt-3">
                          <ImageManager
                            product={editingProduct ?? undefined}
                            existingImages={[]}
                            setExistingImages={() => {}}
                            multiple={true}
                            selectedImages={formData.uploaded_images}
                            onSelectionChange={(images) => setFormData(prev => ({ ...prev, uploaded_images: images }))}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SECCIÓN: Variantes (Tonos/Colores) */}
                  <div className="space-y-6 mb-8 pt-8 border-t border-gray-200">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold">4</div>
                      <h3 className="text-lg font-semibold text-gray-900">Variantes y Tonos</h3>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <label className="flex items-center justify-between cursor-pointer group mb-6">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-6 rounded-full transition-colors ${formData.use_variants ? 'bg-purple-600' : 'bg-gray-300'}`}>
                            <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${formData.use_variants ? 'translate-x-5 translate-y-1' : 'translate-x-1 translate-y-1'}`}></div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900">Habilitar Variantes</span>
                            <p className="text-xs text-gray-500">Activar si el producto tiene múltiples tonos o colores</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.use_variants}
                          onChange={(e) => setFormData(prev => ({ ...prev, use_variants: e.target.checked }))}
                          className="sr-only"
                        />
                      </label>

                      {formData.use_variants && (
                        <div className="space-y-4 animate-fadeIn">
                          {/* Lista de variantes existentes */}
                          {formData.variants.length > 0 && (
                            <div className="grid grid-cols-1 gap-3 mb-4">
                              {formData.variants.map((variant, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                  <div className="flex items-center space-x-3">
                                    <div 
                                      className="w-8 h-8 rounded-full border border-gray-200" 
                                      style={{ backgroundColor: variant.color_hex || '#ccc' }}
                                    />
                                    <div>
                                      <p className="font-medium text-sm text-gray-900">{variant.name}</p>
                                      <p className="text-xs text-gray-400">
                                        Stgo: {getVariantStockByLocation(variant, 'SANTIAGO')} | Antof: {getVariantStockByLocation(variant, 'ANTOFAGASTA')}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    {variant.image && (
                                      <img src={variant.image} alt="Variant" className="w-8 h-8 object-cover rounded" />
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => handleEditVariant(idx)}
                                      className="text-blue-500 hover:text-blue-700 p-1"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveVariant(idx)}
                                      className="text-red-500 hover:text-red-700 p-1"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Formulario para agregar variante */}
                          <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">
                              {editingVariantIndex !== null ? 'Editar Variante' : 'Agregar Nueva Variante'}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <input
                                type="text"
                                placeholder="Nombre (ej. Color)"
                                className="px-3 py-2 border rounded-md text-sm"
                                id="new_variant_name"
                                defaultValue="Color"
                              />
                              {/* 'Valor' input removed */}
                              <div className="flex items-center space-x-2 relative">
                                <div 
                                  className="h-9 w-12 border rounded-md cursor-pointer shadow-sm"
                                  style={{ backgroundColor: newVariantColor }}
                                  onClick={() => setShowColorPicker(!showColorPicker)}
                                />
                                <input
                                  type="hidden"
                                  id="new_variant_color"
                                  value={newVariantColor}
                                />
                                <span className="text-xs text-gray-500">Color del selector</span>
                                
                                {showColorPicker && (
                                  <>
                                    <div 
                                      className="fixed inset-0 z-40" 
                                      onClick={() => setShowColorPicker(false)}
                                    />
                                    <div className="absolute top-10 left-0 z-50 w-72">
                                      <AdvancedColorPicker
                                        color={newVariantColor}
                                        onChange={setNewVariantColor}
                                        onClose={() => setShowColorPicker(false)}
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                              
                              {/* Stock por ubicación para variante */}
                              <div className="grid grid-cols-2 gap-2 col-span-1 md:col-span-2">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Stock Santiago</label>
                                  <input
                                    type="number"
                                    min="0"
                                    placeholder="0"
                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                    id="new_variant_stock_santiago"
                                    defaultValue="0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Stock Antofagasta</label>
                                  <input
                                    type="number"
                                    min="0"
                                    placeholder="0"
                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                    id="new_variant_stock_antofagasta"
                                    defaultValue="0"
                                  />
                                </div>
                              </div>

                              <div className="col-span-1 md:col-span-2">
                                <label className="block text-xs text-gray-500 mb-1">Imagen del Tono</label>
                                <input
                                  type="hidden"
                                  id="new_variant_image"
                                />
                                <MiniImageUploader 
                                  onImageSelect={(url) => {
                                    const input = document.getElementById('new_variant_image') as HTMLInputElement;
                                    if (input) input.value = url;
                                  }}
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <button
                                type="button"
                                onClick={() => {
                                  const nameInput = document.getElementById('new_variant_name') as HTMLInputElement;
                                  // value field removed
                                  const colorInput = document.getElementById('new_variant_color') as HTMLInputElement;
                                  const imageInput = document.getElementById('new_variant_image') as HTMLInputElement;
                                  const stockStgoInput = document.getElementById('new_variant_stock_santiago') as HTMLInputElement;
                                  const stockAntofInput = document.getElementById('new_variant_stock_antofagasta') as HTMLInputElement;

                                  {
                                    const stgo = parseInt(stockStgoInput.value) || 0;
                                    const antof = parseInt(stockAntofInput.value) || 0;
                                    
                                    handleAddVariant({
                                      name: nameInput.value || 'Color',
                                      color_hex: colorInput.value,
                                      image: imageInput.value,
                                      is_active: true,
                                      stock: stgo + antof,
                                      stock_santiago: stgo,
                                      stock_antofagasta: antof,
                                      price_adjustment: 0
                                    });
                                    
                                    // Limpiar solo si no estamos editando (o si terminamos de editar)
                                    if (editingVariantIndex === null) {
                                      imageInput.value = '';
                                      stockStgoInput.value = '0';
                                      stockAntofInput.value = '0';
                                      setNewVariantColor('#000000');
                                    } else {
                                      // Si editamos, limpiamos después de guardar
                                      handleCancelEditVariant();
                                    }
                                  }
                                }}
                                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                                  editingVariantIndex !== null 
                                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {editingVariantIndex !== null ? 'Actualizar Variante' : '+ Agregar Variante'}
                              </button>
                              
                              {editingVariantIndex !== null && (
                                <button
                                  type="button"
                                  onClick={handleCancelEditVariant}
                                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium transition-colors"
                                >
                                  Cancelar
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* SECCIÓN: Estado y Configuración */}
                  <div className="space-y-6 pt-8 border-t border-gray-200">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold">5</div>
                      <h3 className="text-lg font-semibold text-gray-900">Estado y Visibilidad</h3>
                    </div>

                    {/* Switches modernos para estados */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                      <label className="flex items-center justify-between cursor-pointer group">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
                            <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${formData.is_active ? 'translate-x-5 translate-y-1' : 'translate-x-1 translate-y-1'}`}></div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900">Producto Activo</span>
                            <p className="text-xs text-gray-500">Visible en la tienda para clientes</p>
                          </div>
                        </div>
                        <input
                          id="is_active"
                          name="is_active"
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                      </label>

                      <label className="flex items-center justify-between cursor-pointer group pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-6 rounded-full transition-colors ${formData.is_featured ? 'bg-yellow-500' : 'bg-gray-300'}`}>
                            <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${formData.is_featured ? 'translate-x-5 translate-y-1' : 'translate-x-1 translate-y-1'}`}></div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900">Producto Destacado</span>
                            <p className="text-xs text-gray-500">Aparece en sección destacados</p>
                          </div>
                        </div>
                        <input
                          id="is_featured"
                          name="is_featured"
                          type="checkbox"
                          checked={formData.is_featured}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                      </label>

                      <label className="flex items-center justify-between cursor-pointer group pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-6 rounded-full transition-colors ${formData.is_best_selling ? 'bg-blue-500' : 'bg-gray-300'}`}>
                            <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${formData.is_best_selling ? 'translate-x-5 translate-y-1' : 'translate-x-1 translate-y-1'}`}></div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-900">Más Vendido</span>
                            <p className="text-xs text-gray-500">Aparece en productos populares</p>
                          </div>
                        </div>
                        <input
                          id="is_best_selling"
                          name="is_best_selling"
                          type="checkbox"
                          checked={formData.is_best_selling}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Botones de acción fijos en la parte inferior */}
                  <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between mt-8 -mx-6 -mb-6 rounded-b-xl">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-8 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors shadow-lg font-medium flex items-center space-x-2"
                    >
                      <Check className="w-5 h-5" />
                      <span>{editingProduct ? 'Actualizar Producto' : 'Crear Producto'}</span>
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Grid de productos con cards modernos */}
            {!showForm && (
              <>
                {loadingProducts && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                    <p className="text-sm text-gray-600">Cargando productos...</p>
                  </div>
                )}

                <table className="w-full bg-white shadow-sm rounded-lg border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Imagen</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Nombre / SKU</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Estado</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Precio / Descuento</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Stock Santiago</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Stock Antofagasta</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Stock Total</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Categoría</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => {
                      const isLowStock = product.stock <= (product.low_stock_threshold || 5);
                      return (
                      <tr key={product.id} className={`border-b ${isLowStock ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3">
                          {product.image ? (
                            <img
                              className="w-12 h-12 object-cover rounded-md"
                              src={getSupabaseThumbnail(product.image, 100)}
                              alt={product.name}
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-900">{product.name}</span>
                            {product.sku && <span className="text-xs text-gray-500">SKU: {product.sku}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${product.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {product.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            {product.discount_percentage && product.discount_percentage > 0 ? (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span className="text-red-600 font-semibold">{formatCurrency(product.discount_price || product.price)}</span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-black text-white">
                                    -{product.discount_percentage}%
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500 line-through">{formatCurrency(product.price)}</span>
                              </>
                            ) : (
                              <span className="text-gray-900 font-medium">{formatCurrency(product.price)}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">
                            {product.stock_locations?.find((loc: any) => loc.location === 'SANTIAGO')?.quantity ?? 0}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">
                            {product.stock_locations?.find((loc: any) => loc.location === 'ANTOFAGASTA')?.quantity ?? 0}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <span className={`font-bold ${isLowStock ? 'text-red-600' : 'text-gray-900'}`}>{product.total_stock ?? product.stock}</span>
                            {isLowStock && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                Bajo Stock
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-700">
                            {product.categories && product.categories.length > 0
                              ? product.categories.map((c: any) => c.name).join(', ')
                              : 'Sin categoría'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="inline-flex items-center space-x-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-none hover:bg-blue-700 transition-colors duration-200"
                            >
                              <Edit2 className="w-4 h-4" />
                              <span>Editar</span>
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.slug)}
                              className="inline-flex items-center space-x-2 px-4 py-2 text-sm bg-red-600 text-white rounded-none hover:bg-red-700 transition-colors duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Eliminar</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
  ) : activeTab === 'categories' ? (
          <div>
            {/* Encabezado y acción para abrir modal */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Categorías</h2>
              <button
                onClick={() => { setCategoryErrors([]); setCategoryForm({ name: '', slug: '', description: '', is_active: true }); setEditingCategory(null); setShowCategoryModal(true); }}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 shadow-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Nueva categoría</span>
              </button>
            </div>

            {/* Lista de categorías arriba, estilo minimalista */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {loadingCategories && (
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <div className="w-5 h-5 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
                    <span>Cargando categorías...</span>
                  </div>
                </div>
              )}
              {categories.length === 0 ? (
                <p className="p-6 text-gray-600">No hay categorías creadas aún.</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {categories
                    .filter((c: any) => !c.parent) // Root categories
                    .map((root) => (
                      <React.Fragment key={root.id}>
                        {/* Root Category */}
                        <li className="p-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors">
                          <div className="flex items-center space-x-3">
                            <span className="font-bold text-gray-900">{root.name}</span>
                            <span className="text-sm text-gray-500">/{root.slug}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${root.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{root.is_active ? 'Activo' : 'Inactivo'}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditCategory(root)}
                              className="inline-flex items-center space-x-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 border border-blue-200 transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                              <span>Editar</span>
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(root.id)}
                              className="inline-flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-red-700 rounded hover:bg-red-100 border border-red-200 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Eliminar</span>
                            </button>
                          </div>
                        </li>
                        
                        {/* Subcategories */}
                        {categories
                          .filter((c: any) => (c.parent && typeof c.parent === 'object' ? c.parent.id === root.id : c.parent === root.id))
                          .map((child) => (
                            <li key={child.id} className="p-4 flex items-center justify-between bg-gray-50 pl-12 border-l-4 border-gray-100 hover:bg-gray-100 transition-colors">
                              <div className="flex items-center space-x-3">
                                <div className="flex items-center text-gray-500">
                                  <span className="mr-2">↳</span>
                                  <span className="font-medium text-gray-800">{child.name}</span>
                                </div>
                                <span className="text-sm text-gray-500">/{child.slug}</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${child.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{child.is_active ? 'Activo' : 'Inactivo'}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleEditCategory(child)}
                                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 border border-blue-200 transition-colors"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  <span>Editar</span>
                                </button>
                                <button
                                  onClick={() => handleDeleteCategory(child.id)}
                                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-red-700 rounded hover:bg-red-100 border border-red-200 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Eliminar</span>
                                </button>
                              </div>
                            </li>
                          ))}
                      </React.Fragment>
                    ))}
                </ul>
              )}
            </div>

            {/* Modal para crear/editar categoría */}
            {showCategoryModal && (
              <div className="fixed inset-0 z-50">
                <div className="absolute inset-0 bg-black/40" onClick={() => setShowCategoryModal(false)}></div>
                <div className="relative max-w-2xl mx-auto mt-24 bg-white rounded-xl shadow-lg border border-gray-200">
                  <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">{editingCategory ? 'Editar Categoría' : 'Crear Categoría'}</h3>
                    <button onClick={() => setShowCategoryModal(false)} className="text-gray-500 hover:text-gray-700">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={handleCreateCategory} className="p-6 space-y-6">
                    {categoryErrors.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <div className="flex items-start">
                          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                          <div className="ml-3">
                            {categoryErrors.map((err, i) => (
                              <p key={i} className="text-sm text-red-800">{err}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                        <input
                          type="text"
                          value={categoryForm.name}
                          onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200"
                          placeholder="Nombre de la categoría"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
                        <input
                          type="text"
                          value={categoryForm.slug}
                          onChange={(e) => setCategoryForm((prev) => ({ ...prev, slug: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200"
                          placeholder="slug-de-categoria"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Categoría Padre</label>
                      <select
                        value={categoryForm.parent_id || ''}
                        onChange={(e) => setCategoryForm((prev) => ({ ...prev, parent_id: e.target.value ? parseInt(e.target.value) : undefined }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200"
                      >
                        <option value="">Ninguna (Categoría Principal)</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                      <select
                        value={categoryForm.is_active ? 'true' : 'false'}
                        onChange={(e) => setCategoryForm((prev) => ({ ...prev, is_active: e.target.value === 'true' }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200"
                      >
                        <option value="true">Activo</option>
                        <option value="false">Inactivo</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                      <textarea
                         rows={3}
                         value={categoryForm.description}
                         onChange={(e) => setCategoryForm((prev) => ({ ...prev, description: e.target.value }))}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200"
                         placeholder="Descripción (opcional)"></textarea>
                      <div className="flex justify-end mt-4">
                        <button
                          type="submit"
                          disabled={creatingCategory}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {creatingCategory ? (
                            <>
                              <Check className="w-4 h-4" />
                              <span>Creando...</span>
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4" />
                              <span>{editingCategory ? 'Guardar Cambios' : 'Crear Categoría'}</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'services' ? (
          <div>
            <ServiceList branch={selectedBranch} />
          </div>
        ) : activeTab === 'professionals' ? (
          <div>
            <ProfessionalList branch={selectedBranch} />
          </div>
        ) : activeTab === 'bookings' ? (
          <div>
            <BookingsList branch={selectedBranch} />
          </div>
        ) : activeTab === 'clients' ? (
          <div>
            <ClientsList />
          </div>
        ) : activeTab === 'calendar' ? (
          <div>
            <AdminCalendar />
          </div>
        ) : activeTab === 'sales' ? (
          <div>
            <SalesDashboard branch={selectedBranch} />
          </div>
        ) : activeTab === 'content' ? (
          <div>
            <ContentManager />
          </div>
        ) : null}

      </main>
    </div>
  );
}

export default AdminPanel;
